import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './AppUpdateWatcher.scss';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const CURRENT_BUILD_ID = typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : '';

/**
 * Makes sure people run the latest deploy without having to clear caches or
 * use incognito. Tabs stay open for hours (bartender tablets, a customer
 * waiting for an order), and a tab only runs new code after it reloads.
 *
 * It compares this build's id with /version.json (served uncached):
 * - new version found while the tab is in the background or when the user
 *   comes back to it -> reload right away (nothing is being typed then);
 * - new version found while the user is on screen -> reload on the next page
 *   change, and meanwhile show a small "Actualizar" banner.
 * Carts live on the server, so a reload loses nothing.
 */
const AppUpdateWatcher: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [updateReady, setUpdateReady] = useState(false);
  const updateReadyRef = useRef(false);
  const firstPathRef = useRef(location.pathname);

  const isNewVersionAvailable = useCallback(async (): Promise<boolean> => {
    if (updateReadyRef.current) return true;
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) return false;
      const { buildId } = await response.json();
      if (buildId && buildId !== CURRENT_BUILD_ID) {
        updateReadyRef.current = true;
        setUpdateReady(true);
        return true;
      }
    } catch {
      // Offline or a deploy in progress: try again on the next check
    }
    return false;
  }, []);

  useEffect(() => {
    // Dev server has no version.json and reloads by itself
    if (import.meta.env.DEV || !CURRENT_BUILD_ID) return;

    const check = async () => {
      const hasUpdate = await isNewVersionAvailable();
      if (hasUpdate && document.visibilityState === 'hidden') {
        window.location.reload();
      }
    };

    const onVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (await isNewVersionAvailable()) window.location.reload();
    };

    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', check);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', check);
    };
  }, [isNewVersionAvailable]);

  // Changing page is a safe moment: load the new version for the new page
  useEffect(() => {
    if (location.pathname === firstPathRef.current) return;
    firstPathRef.current = location.pathname;
    if (updateReadyRef.current) window.location.reload();
  }, [location.pathname]);

  if (!updateReady) return null;

  return (
    <div className="app-update-banner" role="status">
      <span>{t('appUpdate.available')}</span>
      <button type="button" className="app-update-banner__button" onClick={() => window.location.reload()}>
        {t('appUpdate.reload')}
      </button>
    </div>
  );
};

export default AppUpdateWatcher;
